import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, Lock, Search, Sparkles, ArrowRight, Loader2, Download, LogOut, User, Star, ChevronRight, CreditCard, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------- Layout / shared -------------------- */

export function MemberShell({ children, active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = [
    { to: "/member/hub", label: "Dashboard" },
    { to: "/member/toolbox", label: "Toolbox" },
    { to: "/member/resources", label: "Resources" },
    { to: "/member/bookmarks", label: "Bookmarks" },
    { to: "/member/account", label: "Account" },
  ];
  const doLogout = async () => { await logout(); navigate("/"); toast.success("Signed out."); };
  return (
    <div data-testid="member-shell" className="min-h-[calc(100vh-80px)] bg-slate-50">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-28 h-fit bg-white border border-slate-200 p-6">
          <div className="mb-5">
            <div className="eyebrow mb-3">Signed in as</div>
            <div className="font-semibold text-slate-900 text-sm">{user?.name}</div>
            <div className="text-xs text-slate-500 mt-1 break-all">{user?.email}</div>
            <TierBadge tier={user?.tier} />
          </div>
          <nav className="space-y-1">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.split("/").pop()}`}
                className={`block px-3 py-2 text-sm font-semibold border-l-2 transition-colors ${active === n.to ? "border-brand-orange text-brand-blue bg-slate-50" : "border-transparent text-slate-600 hover:text-brand-blue"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <button data-testid="member-logout" onClick={doLogout} className="mt-6 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-brand-orange border-t border-slate-200 pt-4">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const paid = tier === "paid_member";
  return (
    <div data-testid="tier-badge" className={`mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${paid ? "bg-brand-orange text-white" : "bg-slate-200 text-slate-700"}`}>
      {paid ? <><Star className="w-3 h-3" /> Paid member</> : "Free"}
    </div>
  );
}

/* -------------------- Pricing / upgrade UI -------------------- */

export function UpgradePlans({ compact = false }) {
  const { startCheckout } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/pricing`).then(({ data }) => setPricing(data)).catch(() => setPricing(null));
  }, []);

  const go = async (lookup) => {
    setBusy(lookup);
    try { await startCheckout(lookup); }
    catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  if (!pricing) return null;

  const plans = [
    { key: "member_monthly", tag: "Monthly", price: pricing.monthly.display_amount, per: "per month", note: "Cancel anytime, full access while active.", data: pricing.monthly },
    { key: "member_yearly", tag: "Annual", price: pricing.yearly.display_amount, per: "per year", note: `Save ${pricing.yearly.savings_pct}% versus monthly.`, data: pricing.yearly, featured: true },
  ];

  return (
    <div data-testid="upgrade-plans" className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${compact ? "" : "mt-6"}`}>
      {plans.map((p) => (
        <div key={p.key} data-testid={`plan-${p.key}`} className={`p-6 border ${p.featured ? "border-brand-orange bg-white" : "border-slate-200 bg-white"} relative`}>
          {p.featured && <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest font-bold bg-brand-orange text-white px-2 py-1">Best value</span>}
          <div className="eyebrow text-brand-orange mb-3">{p.tag}</div>
          <div className="flex items-baseline gap-1 mb-3">
            <span className="font-serif text-4xl text-brand-blue">{p.price}</span>
            <span className="text-sm text-slate-500 ml-1">{p.per}</span>
          </div>
          <p className="text-xs text-slate-600 mb-5">{p.note}</p>
          <ul className="text-xs text-slate-700 space-y-2 mb-6">
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> Every editable template and tool</li>
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> New resources added quarterly</li>
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> Practitioner forum access</li>
          </ul>
          <button data-testid={`checkout-${p.key}`} onClick={() => go(p.key)} disabled={busy === p.key} className={`w-full justify-center inline-flex items-center gap-2 px-5 py-3 font-semibold text-sm ${p.featured ? "bg-brand-orange text-white hover:-translate-y-0.5 transition-transform" : "border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"}`}>
            {busy === p.key ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><CreditCard className="w-4 h-4" /> Choose {p.tag}</>}
          </button>
        </div>
      ))}
    </div>
  );
}

/* -------------------- Dashboard -------------------- */

export function MemberHub() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [r, b] = await Promise.all([
          axios.get(`${API}/resources`),
          axios.get(`${API}/bookmarks`),
        ]);
        setResources(r.data.resources || []);
        setBookmarks(b.data.bookmarks || []);
      } catch { /* handled elsewhere */ }
    })();
  }, []);

  const recent = useMemo(() => [...resources].sort((a, b) => (b.new === true) - (a.new === true) || b.updated_at.localeCompare(a.updated_at)).slice(0, 3), [resources]);

  return (
    <MemberShell active="/member/hub">
      <div data-testid="member-hub-page">
        <div className="eyebrow mb-3">Member Hub</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-3">Welcome back, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-slate-600 max-w-2xl mb-10">Templates, guides and tools drawn directly from Andrea's advisory practice. Your resources, your bookmarks, your account - in one place.</p>

        {user?.tier !== "paid_member" && (
          <div data-testid="upgrade-banner" className="bg-brand-blue text-white p-8 mb-10">
            <div className="max-w-3xl">
              <div className="eyebrow text-brand-yellow mb-2">Upgrade</div>
              <h2 className="font-serif text-2xl leading-snug mb-2">Unlock every template and tool.</h2>
              <p className="text-white/80 text-sm">Paid membership opens full editable templates, tools, live forums and coaching add-ons. Secure Stripe checkout, cancel anytime from your account.</p>
            </div>
            <UpgradePlans />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          <Panel title="New & updated" testid="panel-new">
            {recent.length === 0 && <Empty label="No resources yet." />}
            {recent.map((r) => <ResourceRow key={r.slug} r={r} />)}
          </Panel>
          <Panel title="Your bookmarks" testid="panel-bookmarks" actionTo="/member/bookmarks" actionLabel="View all">
            {bookmarks.length === 0 && <Empty label="Nothing bookmarked yet - start exploring resources." />}
            {bookmarks.slice(0, 4).map((r) => <ResourceRow key={r.slug} r={r} />)}
          </Panel>
        </div>

        <Panel title="Popular categories" testid="panel-categories">
          <div className="flex flex-wrap gap-2 pt-1">
            {[...new Set(resources.map((r) => r.category))].map((c) => (
              <Link key={c} to={`/member/resources?cat=${encodeURIComponent(c)}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-2 border border-slate-200 hover:border-brand-orange hover:text-brand-orange">
                {c}
              </Link>
            ))}
          </div>
        </Panel>
      </div>
    </MemberShell>
  );
}

function Panel({ title, testid, actionTo, actionLabel, children }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-7">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-lg text-brand-blue">{title}</h3>
        {actionTo && <Link to={actionTo} className="text-xs uppercase tracking-widest font-semibold text-brand-orange">{actionLabel} →</Link>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-sm text-slate-500 border border-dashed border-slate-200 p-5 text-center">{label}</div>;
}

function ResourceRow({ r }) {
  return (
    <Link to={`/member/resources/${r.slug}`} data-testid={`row-${r.slug}`} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0 hover:text-brand-orange transition-colors">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">{r.title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{r.category} · updated {r.updated_at}</div>
      </div>
      <div className="flex items-center gap-2">
        {r.new && <span className="text-[10px] uppercase tracking-widest font-bold bg-brand-yellow text-brand-blue px-2 py-1">New</span>}
        {r.tier === "paid" && !r.unlocked && <Lock className="w-4 h-4 text-slate-400" />}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </Link>
  );
}

/* -------------------- Resources list -------------------- */

export function MemberResources() {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    const preset = new URLSearchParams(window.location.search).get("cat");
    if (preset) setCat(preset);
    axios.get(`${API}/resources`).then(({ data }) => {
      setResources(data.resources || []); setCategories(data.categories || []);
    });
  }, []);

  const filtered = useMemo(() => resources.filter((r) =>
    (cat === "all" || r.category === cat) &&
    (q.trim() === "" || (r.title + r.summary + r.category).toLowerCase().includes(q.toLowerCase()))
  ), [resources, cat, q]);

  return (
    <MemberShell active="/member/resources">
      <div data-testid="member-resources-page">
        <div className="eyebrow mb-3">Resources</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-6">The full library.</h1>
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="resources-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates, tools, guides…" className="w-full pl-10 pr-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:border-brand-blue" />
          </div>
          <select data-testid="resources-category" value={cat} onChange={(e) => setCat(e.target.value)} className="border border-slate-300 px-4 py-3 text-sm bg-white">
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <Link key={r.slug} to={`/member/resources/${r.slug}`} data-testid={`res-card-${r.slug}`} className="bg-white border border-slate-200 p-6 hover:border-brand-orange transition-colors">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">{r.category}</span>
                <div className="flex items-center gap-2">
                  {r.new && <span className="text-[10px] uppercase tracking-widest font-bold bg-brand-yellow text-brand-blue px-2 py-0.5">New</span>}
                  {r.tier === "paid" && (r.unlocked ? <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Unlocked</span> : <Lock className="w-3.5 h-3.5 text-slate-400" />)}
                  {r.tier === "free" && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Free</span>}
                </div>
              </div>
              <h3 className="font-serif text-lg text-brand-blue mb-2 leading-snug">{r.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{r.summary}</p>
            </Link>
          ))}
          {filtered.length === 0 && <Empty label="No resources match your filter." />}
        </div>
      </div>
    </MemberShell>
  );
}

/* -------------------- Resource detail -------------------- */

export function MemberResourceDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/${slug}`);
      setR(data);
    } catch (e) {
      toast.error("Could not load resource.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const toggleBookmark = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookmarks/${slug}`);
      setR({ ...r, bookmarked: data.bookmarked });
      toast.success(data.bookmarked ? "Bookmarked." : "Removed bookmark.");
    } catch { toast.error("Bookmark failed."); }
    finally { setBusy(false); }
  };

  if (!r) return <MemberShell active="/member/resources"><div className="text-slate-500">Loading…</div></MemberShell>;

  return (
    <MemberShell active="/member/resources">
      <div data-testid={`resource-detail-${slug}`}>
        <Link to="/member/resources" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange">← Back to resources</Link>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">{r.category}</span>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${r.tier === "free" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{r.tier === "free" ? "Free" : "Paid member"}</span>
          {r.unlocked && r.tier === "paid" && <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Unlocked</span>}
        </div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mt-4">{r.title}</h1>
        <p className="text-slate-600 mt-4 max-w-3xl leading-relaxed">{r.summary}</p>

        <div className="flex flex-wrap gap-3 mt-6">
          <button data-testid="bookmark-toggle" onClick={toggleBookmark} disabled={busy} className="btn-secondary">
            {r.bookmarked ? <><BookmarkCheck className="w-4 h-4" /> Bookmarked</> : <><Bookmark className="w-4 h-4" /> Bookmark</>}
          </button>
          {r.unlocked && <button data-testid="download-resource" className="btn-primary" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              const { data } = await axios.post(`${API}/resources/${slug}/download-link`);
              const a = document.createElement("a");
              a.href = data.download_url;
              a.rel = "noreferrer";
              a.download = data.filename || `${slug}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              toast.success("Your download is starting.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not start download.");
            } finally { setBusy(false); }
          }}><Download className="w-4 h-4" /> Download PDF</button>}
        </div>

        <div className="mt-10 bg-white border border-slate-200 p-8">
          <div className="eyebrow mb-3">Preview</div>
          <p className="text-slate-700 leading-relaxed">{r.preview_body}</p>
        </div>

        {r.unlocked ? (
          <div data-testid="resource-full-body" className="mt-4 bg-white border border-slate-200 p-8">
            <div className="eyebrow mb-3">Full resource</div>
            <p className="text-slate-700 leading-relaxed">{r.full_body}</p>
          </div>
        ) : (
          <div data-testid="resource-gate" className="mt-4 bg-brand-blue text-white p-10">
            <div className="text-center mb-6">
              <Lock className="w-8 h-8 text-brand-yellow mx-auto mb-4" />
              <h3 className="font-serif text-2xl mb-3">Paid members only</h3>
              <p className="text-white/80 max-w-md mx-auto">Upgrade to unlock the full editable resource plus every other template, tool and guide in the library.</p>
            </div>
            <UpgradePlans compact />
            <div className="text-xs text-white/50 uppercase tracking-widest mt-6 text-center">Secure payment via Stripe</div>
          </div>
        )}
      </div>
    </MemberShell>
  );
}

/* -------------------- Bookmarks & Account -------------------- */

export function MemberBookmarks() {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/bookmarks`).then(({ data }) => setItems(data.bookmarks || [])); }, []);
  return (
    <MemberShell active="/member/bookmarks">
      <div data-testid="member-bookmarks-page">
        <div className="eyebrow mb-3">Bookmarks</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-8">Saved resources.</h1>
        {items.length === 0 && <Empty label="No bookmarks yet - open any resource and tap Bookmark." />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((r) => (
            <Link key={r.slug} to={`/member/resources/${r.slug}`} data-testid={`bm-${r.slug}`} className="bg-white border border-slate-200 p-6 hover:border-brand-orange">
              <div className="text-[10px] uppercase tracking-widest font-bold text-brand-orange mb-2">{r.category}</div>
              <div className="font-serif text-lg text-brand-blue mb-1">{r.title}</div>
              <div className="text-sm text-slate-600">{r.summary}</div>
            </Link>
          ))}
        </div>
      </div>
    </MemberShell>
  );
}

export function MemberAccount() {
  const { user, openPortal } = useAuth();
  const [busy, setBusy] = useState(false);
  const paid = user?.tier === "paid_member";
  const manageBilling = async () => {
    setBusy(true);
    try { await openPortal(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing portal."); }
    finally { setBusy(false); }
  };
  return (
    <MemberShell active="/member/account">
      <div data-testid="member-account-page">
        <div className="eyebrow mb-3">Account</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-8">Your details.</h1>

        <div className="bg-white border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-brand-orange" />
            <h2 className="font-serif text-xl text-brand-blue">Profile</h2>
          </div>
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <Row label="Role" value={user?.role} />
          <Row label="Member since" value={(user?.created_at || "").slice(0, 10)} />
        </div>

        <div className="bg-white border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-brand-orange" />
            <h2 className="font-serif text-xl text-brand-blue">Subscription</h2>
          </div>
          <Row label="Current tier" value={paid ? "Paid member" : "Free"} />
          {user?.subscription_status && <Row label="Billing status" value={user.subscription_status} />}
          {paid && (
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="manage-billing" onClick={manageBilling} disabled={busy} className="btn-primary">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</> : <><CreditCard className="w-4 h-4" /> Manage billing on Stripe</>}
              </button>
              {user?.role === "admin" && (
                <span className="text-xs uppercase tracking-widest text-slate-500 self-center">Admin account · comp'd membership</span>
              )}
            </div>
          )}
          {!paid && (
            <div className="mt-6">
              <div className="eyebrow mb-3">Choose a plan</div>
              <UpgradePlans compact />
            </div>
          )}
        </div>
      </div>
    </MemberShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 py-3 border-b border-slate-100 last:border-b-0">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}
